// ENG-7435: value-blind canonicalisation of the effective sender_policy before
// it enters any restart / credential-rewrite hash.
//
// Root cause of the ~10-min respawn: the server (/host/refresh) resolves the
// team_only principal id lists (slack_user_ids / telegram_chat_ids /
// teams_aad_object_ids) from an unordered `organization_people` join, so the
// SAME logical principal SET arrived in a different array order each refresh.
// `canonicalJson` sorts object keys but deliberately preserves array order
// (semantic arrays like `allowed_users` depend on it), so that instability
// flowed straight into the sender_policy restart hash and the channel-config
// hash — the hash oscillated (returning to prior values) and bounced the
// agent's session on every provisioning cycle even though the policy never
// changed.
//
// The server-side ordering fix (principal-resolution.ts / host-runtime.ts)
// removes the source of the disorder. This helper is the belt-and-braces guard
// on the consumer side — the same "value-blind hash" posture the managed-MCP
// structure hash uses to absorb Composio URL rotation (ENG-5285/ENG-5537) — so
// a set reorder from ANY resolver (including an API instance that predates the
// server fix) can never again cause a false restart.
//
// Scoped intentionally to the three team_principals id LISTS, which are sets
// whose order carries no meaning. Every other sender_policy field is a scalar
// (mode / team_id / source / internal_only) or the single-id `principal`
// subobject — left untouched.

import { createHash } from 'node:crypto';

import { canonicalJson } from './canonical-json.js';

const PRINCIPAL_ID_LIST_KEYS = [
  'slack_user_ids',
  'telegram_chat_ids',
  'teams_aad_object_ids',
] as const;

/**
 * Return a copy of `policy` with its `team_principals` id lists sorted, so two
 * policies that differ only in principal-set order canonicalise identically.
 * Pure: never mutates the input (the caller still ships the raw, unsorted
 * arrays as env vars — the MCP filter treats them as a set regardless).
 * Non-object / null / no-team_principals inputs pass through unchanged.
 */
export function normalizeSenderPolicyForHash(policy: unknown): unknown {
  if (policy === null || typeof policy !== 'object') return policy;
  const p = policy as Record<string, unknown>;
  const tp = p.team_principals;
  if (tp === null || typeof tp !== 'object') return policy;

  const src = tp as Record<string, unknown>;
  const normalizedTeamPrincipals: Record<string, unknown> = { ...src };
  for (const key of PRINCIPAL_ID_LIST_KEYS) {
    const list = src[key];
    if (Array.isArray(list)) {
      // Copy before sort — Array.prototype.sort mutates in place, and the raw
      // arrays are still referenced by the credential-write path.
      normalizedTeamPrincipals[key] = [...list].sort();
    }
  }
  return { ...p, team_principals: normalizedTeamPrincipals };
}

/**
 * Compute the sha256 the manager keys the sender_policy restart trigger on.
 * Wraps the policy in `{ senderPolicy }` (unchanged from the original inline
 * form) after value-blind normalisation, so an unchanged policy hashes
 * identically every tick and `decideSenderPolicyRestart` debounces it.
 */
export function computeSenderPolicyRestartHash(policy: unknown): string {
  return createHash('sha256')
    .update(canonicalJson({ senderPolicy: normalizeSenderPolicyForHash(policy) }))
    .digest('hex');
}

/**
 * ENG-7771: does this effective sender_policy actually restrict who can talk
 * to the agent? Gates the first-poll fail-safe restart: when the manager has
 * no delivery baseline for an agent (transition deploy, lost/corrupt sidecar)
 * it cannot know what policy the running session enforces. If the desired
 * policy is restrictive, the only safe move is to restart the session (fail
 * closed - bounded restart cost vs unbounded exposure). If it is not
 * restrictive, there is no exposure and baselining without a restart avoids
 * respawning the whole fleet on the deploy that ships the sidecar.
 *
 * Fail-closed by construction: only an explicit `mode: 'all'` without
 * `internal_only` (or a null/absent policy, which the API resolves to 'all')
 * classifies as non-restrictive. Unknown or future modes classify as
 * restrictive, so growth of the SENDER_POLICY_MODES enum (which happened
 * twice this quarter, ENG-7757) can never silently escape the fail-safe. A
 * malformed non-object policy also classifies restrictive.
 */
export function isSenderPolicyRestrictive(policy: unknown): boolean {
  if (policy === null || policy === undefined) return false;
  if (typeof policy !== 'object') return true;
  const p = policy as Record<string, unknown>;
  if (p.internal_only === true) return true;
  return p.mode !== 'all';
}
